/**
 * usePageGuards Hook
 * Shared page logic that runs on every page load
 * - Checks if main tenant is partner
 * - Checks if user has agreed to all terms
 * - Redirects to profile page if terms not agreed
 */

import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTenantContext } from 'contexts/TenantContext';
import { checkLegalAgreementsCompliance, isProtectedRoute, getProfilePageUrl, isLegalPage } from 'utils/legalAgreementsGuard';
import logger from 'utils/logger';

// Detect if we're in platform-public (ports 4001 or 4003)
const isPlatformPublic = typeof window !== 'undefined' && (
  window.location.port === '4001' || 
  window.location.port === '4003'
);

// Safely import MSAL - may not be available in platform-public
let useMsalHook = null;
try {
  // Try to import MSAL - it may not be available in platform-public
  const msalReact = require('@azure/msal-react');
  useMsalHook = msalReact.useMsal;
} catch (error) {
  // MSAL not available - this is expected for platform-public
  logger.debug('PAGE-GUARDS', 'MSAL module not available, will skip auth checks');
}

/**
 * Dummy hook that returns safe defaults
 * Used when MSAL is not available to satisfy Rules of Hooks
 */
function useMsalDummy() {
  return { instance: null, accounts: [] };
}

/**
 * Safe MSAL hook - returns null values for platform-public
 * This hook must be called unconditionally (Rules of Hooks)
 * CRITICAL: Always call a hook (either real MSAL or dummy), never conditionally
 */
function useMsalSafe() {
  // Always call a hook - either the real MSAL hook or a dummy hook
  // This satisfies React's Rules of Hooks (always call hooks in same order)
  const hookToCall = useMsalHook || useMsalDummy;
  
  try {
    // Always call the hook unconditionally
    return hookToCall();
  } catch (error) {
    // If hook fails (e.g., no MSAL provider), return safe defaults
    logger.debug('PAGE-GUARDS', 'MSAL hook failed, using safe defaults', { error: error.message });
    return { instance: null, accounts: [] };
  }
}

/**
 * Hook for page guards logic
 * Checks partner status and terms agreement compliance
 * @returns {{ isChecking: boolean, isPartner: boolean, isTermsCompliant: boolean }}
 */
export function usePageGuards() {
  // Use safe MSAL wrapper - always returns values (null for platform-public)
  const { instance, accounts } = useMsalSafe();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMainTenantPartner } = useTenantContext();
  
  // For platform-public (no MSAL), immediately set checking to false
  const [isChecking, setIsChecking] = useState(() => {
    // If we're in platform-public or have no MSAL instance, start with checking=false
    if (isPlatformPublic) {
      return false;
    }
    return true;
  });
  const [isTermsCompliant, setIsTermsCompliant] = useState(() => {
    // For platform-public, always compliant (no auth required)
    if (isPlatformPublic) {
      return true;
    }
    return false;
  });
  const [hasChecked, setHasChecked] = useState(() => {
    // For platform-public, mark as checked immediately
    if (isPlatformPublic) {
      return true;
    }
    return false;
  });
  const [lastAccountId, setLastAccountId] = useState(null);
  const lastCheckTimeRef = useRef(0);
  const CHECK_COOLDOWN_MS = 5000; // 5 seconds cooldown between checks (increased for better performance)

  // Helper to check if onboarding is active (memoized to prevent unnecessary checks)
  const isOnboardingActiveRef = useRef(() => {
    const overlay = document.querySelector('.onboarding-overlay');
    return overlay && (
      overlay.classList.contains('visible') || 
      getComputedStyle(overlay).opacity !== '0'
    );
  });

  // Reset hasChecked when accounts change (user logs in/out)
  useEffect(() => {
    // Skip if onboarding is active - prevents flickering during intro
    if (isOnboardingActiveRef.current()) {
      setIsChecking(false);
      setIsTermsCompliant(true);
      return;
    }

    const currentAccountId = accounts && accounts.length > 0
      ? (accounts[0]?.homeAccountId || accounts[0]?.localAccountId || accounts[0]?.username)
      : null;
    
    if (currentAccountId !== lastAccountId) {
      setLastAccountId(currentAccountId);
      setHasChecked(false); // Reset check flag when user changes
      setIsChecking(true); // Start checking again
    }
  }, [accounts, lastAccountId]);

  useEffect(() => {
    const performChecks = async () => {
      // CRITICAL: For platform-public, immediately skip all checks
      if (isPlatformPublic) {
        setIsChecking(false);
        setIsTermsCompliant(true);
        setHasChecked(true);
        return;
      }
      
      // Skip checks if onboarding overlay is active - prevents flickering during intro
      if (isOnboardingActiveRef.current()) {
        setIsChecking(false);
        setIsTermsCompliant(true); // Allow access during onboarding
        return;
      }

      // Skip checks if user is not authenticated
      if (!instance || !accounts || accounts.length === 0) {
        setIsChecking(false);
        setIsTermsCompliant(true); // Allow unauthenticated users
        return;
      }

      // Only check protected routes
      const pathname = location.pathname;
      const isProtected = isProtectedRoute(pathname);

      if (!isProtected) {
        // Public routes don't need compliance - exit early and don't check
        setIsChecking(false);
        setIsTermsCompliant(true);
        setHasChecked(true); // Mark as checked to prevent re-triggering
        return;
      }

      // Cooldown: prevent rapid re-checks (e.g., during redirect loops)
      const now = Date.now();
      const timeSinceLastCheck = now - lastCheckTimeRef.current;
      if (timeSinceLastCheck < CHECK_COOLDOWN_MS && hasChecked) {
        // Skip silently if cooldown is active and we've already checked
        return;
      }
      lastCheckTimeRef.current = now;

      // Skip redundant checks: if already compliant and on same session, don't re-check
      // This reduces unnecessary API calls while still checking on route changes
      if (isTermsCompliant && hasChecked && timeSinceLastCheck < CHECK_COOLDOWN_MS * 2) {
        // Already compliant and recently checked - skip
        return;
      }

      // Check legal agreements immediately - doesn't depend on tenant context
      // This prevents infinite loading when tenantLoading is true
      // Pass MSAL instance to check authentication before making API call
      // NOTE: Client-side checks are UX only - server enforces security
      try {
        logger.debug('PAGE-GUARDS', 'Checking legal agreements compliance', {
          pathname
        });
        
        let compliant = await checkLegalAgreementsCompliance(instance);
        
        // If not compliant and user is authenticated, retry once after delay
        // This handles race conditions where user is being created
        if (!compliant && instance?.getAllAccounts()?.length > 0) {
          logger.debug('PAGE-GUARDS', 'User not compliant, retrying after delay (might be new user creation race condition)');
          await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
          compliant = await checkLegalAgreementsCompliance(instance);
          logger.debug('PAGE-GUARDS', 'Retry result', { compliant });
        }
        
        setIsTermsCompliant(compliant);
        
        logger.debug('PAGE-GUARDS', 'Compliance check result', {
          compliant,
          pathname,
          willRedirect: !compliant && pathname !== getProfilePageUrl()
        });

        // If not compliant and on a protected route, redirect to profile immediately
        // NOTE: Server will also enforce this, but client-side redirect provides better UX
        // CRITICAL: Don't redirect if user is on a legal page - they need to read the agreements first
        const isLegalPagePath = isLegalPage(pathname);
        if (!compliant && !isLegalPagePath) {
          logger.debug('PAGE-GUARDS', 'User has not agreed to all legal terms, redirecting to profile page');
          const profileUrl = getProfilePageUrl();
          
          // Ensure we're not already on profile page to prevent redirect loops
          if (pathname !== profileUrl && pathname !== `${profileUrl}/`) {
            try {
              navigate(profileUrl, {
                replace: true,
                state: {
                  from: pathname,
                  reason: 'legal_agreements_required'
                }
              });
            } catch (navError) {
              logger.error('PAGE-GUARDS', 'Failed to navigate to profile page', { error: navError.message });
              // Fallback: use window.location as last resort
              window.location.href = profileUrl;
            }
          } else {
            logger.debug('PAGE-GUARDS', 'Already on profile page, skipping redirect to prevent loop');
          }
          setHasChecked(true);
          setIsChecking(false); // Set checking to false after redirect is initiated
          return; // Exit early after redirect
        } else if (!compliant && isLegalPagePath) {
          logger.debug('PAGE-GUARDS', 'User is on legal page and not compliant - allowing them to read agreements without redirect');
          // Don't redirect - let them read the legal pages
          setHasChecked(true);
          setIsChecking(false);
          return; // Exit early but don't redirect
        }
        
        // If compliant, continue with other checks (partner status, etc.)
        // Wait for tenant context only if needed for partner status checks
        // For now, we'll mark as checked and allow access
        setHasChecked(true);
        setIsChecking(false);
      } catch (error) {
        logger.error('PAGE-GUARDS', 'Error checking legal agreements compliance', { error: error.message });
        
        // Only redirect if we're certain the user hasn't accepted
        // If checkLegalAgreementsCompliance returns true on error (to be safe),
        // we should respect that and not redirect
        const profileUrl = getProfilePageUrl();
        
        // CRITICAL: Don't redirect if user is on a legal page - they need to read the agreements first
        const isLegalPagePath = isLegalPage(pathname);
        
        // Only redirect if we're not already on profile, not on a legal page, and the error suggests non-compliance
        // But be conservative - if we're uncertain, allow access
        const shouldRedirect = pathname !== profileUrl && 
                               pathname !== `${profileUrl}/` &&
                               !isLegalPagePath &&
                               !error?.message?.includes('transient') &&
                               !error?.message?.includes('cache');
        
        if (shouldRedirect) {
          logger.warn('PAGE-GUARDS', 'Redirecting due to compliance check error (non-transient)');
          setIsTermsCompliant(false);
          try {
            navigate(profileUrl, {
              replace: true,
              state: {
                from: pathname,
                reason: 'legal_agreements_check_failed'
              }
            });
          } catch (navError) {
            logger.error('PAGE-GUARDS', 'Failed to navigate to profile page', { error: navError.message });
            // Fallback: use window.location as last resort
            window.location.href = profileUrl;
          }
        } else {
          if (isLegalPagePath) {
            logger.debug('PAGE-GUARDS', 'User is on legal page and error occurred - allowing them to read agreements without redirect');
          } else {
            logger.debug('PAGE-GUARDS', 'Allowing access despite error (transient or already on profile)');
          }
          setIsTermsCompliant(true); // Allow access if uncertain or on legal page
        }
        
        setHasChecked(true);
        setIsChecking(false); // Always set checking to false, even on error
      }
    };

    performChecks();
  }, [instance, accounts, location.pathname, navigate]);

  // Reset hasChecked when route changes (but skip if onboarding is active or on profile page)
  useEffect(() => {
    // CRITICAL: For platform-public, immediately skip all checks
    if (isPlatformPublic) {
      setIsChecking(false);
      setIsTermsCompliant(true);
      setHasChecked(true);
      return;
    }
    
    // Skip if onboarding is active - prevents flickering during intro navigation
    if (isOnboardingActiveRef.current()) {
      setIsChecking(false);
      setIsTermsCompliant(true);
      return;
    }

    // Skip checks if user is not authenticated - don't show "Verifying access" for logged out users
    if (!instance || !accounts || accounts.length === 0) {
      setIsChecking(false);
      setIsTermsCompliant(true); // Allow unauthenticated users
      return;
    }

    // CRITICAL: Don't reset checks when on profile page or payment profile page - they're public routes and causes loops
    const pathname = location.pathname;
    const profileUrl = getProfilePageUrl();
    const isOnProfile = pathname === profileUrl || pathname === `${profileUrl}/` || pathname.startsWith(`${profileUrl}/`);
    const isOnPaymentProfile = pathname.startsWith('/payment-profile');
    
    if (isOnProfile || isOnPaymentProfile) {
      // Already on profile or payment profile - don't reset checks to prevent loops
      setIsChecking(false);
      setIsTermsCompliant(true); // Allow access to profile/payment profile
      setHasChecked(true); // Mark as checked
      return;
    }

    // Only reset for protected routes
    const isProtected = isProtectedRoute(pathname);
    if (isProtected) {
      setHasChecked(false);
      setIsChecking(true); // Start checking when route changes to protected route
    } else {
      // Public route - no need to check
      setIsChecking(false);
      setIsTermsCompliant(true);
      setHasChecked(true);
    }
  }, [location.pathname, instance, accounts]);

  return {
    isChecking,
    isPartner: isMainTenantPartner,
    isTermsCompliant
  };
}

